import React from "react";
//#region types
import { FormEnums, StyleEnums } from "models/enums";
import { InvoiceFormTypes } from "models/formTypes";
import { AutoCompleteExtraProps, FormFieldDefinitionType } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { MessageBubbleDetails } from "models/propTypes/messagePropTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#region components
import { useInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

function useQuotationSendMailFormFields(
  customerId: number,
  serialNumberTemplateValueDisplayText: string,
  invoiceId?: number,
  mailForm?: WintSuperkollAPIModel.InvoiceDefaultMailOptions,
  isNotSendForm?: boolean,
  loadingPropKey?: string
): FormFieldDefinitionType<InvoiceFormTypes.SendMail> {
  const invoiceService = useInvoiceService();
  const [mailToList, setMailToList] = React.useState<AutocompleteListItem[]>([]);
  const [mailFromList, setMailFromList] = React.useState<AutocompleteListItem[]>([]);

  React.useEffect(() => {
    const uniqueMailToList = new Set<string>();

    setMailToList(
      mailForm?.mailToList?.flatMap((item, index): AutocompleteListItem[] =>
        UtilsHelper.splitMultipleEmails(item.address)
          .filter((email) => {
            if (uniqueMailToList.has(email)) {
              return false;
            }
            uniqueMailToList.add(email);
            return true;
          })
          .map((email) => ({
            id: item.type,
            value: email ?? "",
            icon: IconDefinitionConstants.WUser,
          }))
      ) ?? []
    );

    setMailFromList(
      mailForm?.mailFromList
        ?.filter((m) => !isNotSendForm || m.type !== WintSuperkollAPIModel.SendInvoiceMailsFromEnum.PersonMail)
        ?.map(
          (item, index): AutocompleteListItem => ({
            id: item.type,
            value: item.address ?? "",
            description: item.name,
            icon: IconDefinitionConstants.WUser,
          })
        ) ?? []
    );
  }, [mailForm]);

  const onSearchMailTo = async (value?: string) => {
    if (value) {
      const result = await invoiceService.searchReferencePersonsByMail(customerId, value);
      return result.map(
        (x): AutocompleteListItem => ({
          id: WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other,
          value: x.mail ?? "",
          group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
          icon: IconDefinitionConstants.WUser,
        })
      );
    } else return [];
  };

  const fromMailCustomMessage = async (values: InvoiceFormTypes.SendMail, touched: boolean) => {
    const message: MessageBubbleDetails | undefined = values.emailFrom
      ? values.emailFromType === WintSuperkollAPIModel.SendInvoiceMailsFromEnum.Superkoll
        ? touched
          ? {
              type: StyleEnums.DisplayType.Success,
              message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MailSendFromSuccess),
            }
          : undefined
        : {
            type: StyleEnums.DisplayType.Warning,
            message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.MailSendFromWarning),
          }
      : undefined;
    return message;
  };

  const getFieldLoading = loadingPropKey ? (values: any) => values[loadingPropKey] !== false : undefined;

  return {
    emailFrom: {
      label: TranslationKeys.Invoice.SendEmailFrom,
      type: FormEnums.FormFieldType.AUTOCOMPLETE,
      descriptionText: TranslationKeys.Quotation.SendMailFromDescription,
      customMessageHandler: fromMailCustomMessage,
      loading: getFieldLoading,
      extraProps: {
        referenceFieldName: FormFieldConstants.Invoice.SendMail.EmailFromType,
        suggestedItems: mailFromList,
      } as AutoCompleteExtraProps<InvoiceFormTypes.SendMail>,
    },
    emailTo: {
      label: TranslationKeys.Invoice.SendEmailTo,
      type: FormEnums.FormFieldType.MULTIPLEINPUT,
      innerFields: {
        value: {
          label: TranslationKeys.Invoice.SendEmailTo,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          descriptionText: TranslationKeys.Quotation.SendMailToDescription,
          loading: getFieldLoading,
          extraProps: {
            referenceFieldName: "id",
            items: onSearchMailTo,
            suggestedItems: mailToList,
            freeSolo: true,
          } as AutoCompleteExtraProps<AutocompleteListItem>,
        },
      },
    },
    emailSubject: {
      label: TranslationKeys.Invoice.SendEmailSubject,
      type: FormEnums.FormFieldType.TEXT,
      loading: getFieldLoading,
      descriptionText: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Quotation.SendMailSubjectDescription,
        "",
        {
          serialNumber: `${invoiceId ?? 0}`,
          templateText: serialNumberTemplateValueDisplayText,
        }
      ),
    },
    emailMessage: {
      label: TranslationKeys.Common.Message,
      type: FormEnums.FormFieldType.TEXTAREA,
      icon: IconDefinitionConstants.WCommentDots,
      descriptionText: TranslationKeys.Quotation.SendMailMessageDescription,
      loading: getFieldLoading,
    },
  };
}

export default useQuotationSendMailFormFields;
